import React, { useState } from "react";
import { motion } from "framer-motion";
import { Compass, ArrowRight, Check } from "lucide-react";
import { Button } from "../ui/button";

export const LIFE_STAGES = [
  { v: "student", l: "Student" },
  { v: "early_career", l: "Early career" },
  { v: "young_couple", l: "Young couple" },
  { v: "parent", l: "Parent / family" },
  { v: "pre_retirement", l: "Pre-retirement" },
  { v: "retired", l: "Retired" },
];
export const HOUSEHOLDS = [
  { v: "single", l: "Single" },
  { v: "couple", l: "Couple" },
  { v: "family", l: "Family with dependents" },
];
export const PRIORITIES = [
  "Saving for a deposit",
  "Managing debt",
  "Building a pension",
  "Budgeting as a family",
  "Building an emergency fund",
  "Investing & growing wealth",
];

export const Chip = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm transition-colors ${
      active
        ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-200"
        : "border-slate-700 bg-slate-900/60 text-slate-300 hover:border-slate-600 hover:bg-slate-800/60"
    }`}
  >
    {active && <Check className="w-3.5 h-3.5" />}
    {children}
  </button>
);

export const ProfileSetup = ({ initial, submitting, onComplete }) => {
  const [lifeStage, setLifeStage] = useState(initial?.life_stage || null);
  const [household, setHousehold] = useState(initial?.household_type || null);
  const [priorities, setPriorities] = useState(initial?.priorities || []);

  const togglePriority = (p) =>
    setPriorities((ps) => (ps.includes(p) ? ps.filter((x) => x !== p) : [...ps, p]));

  const submit = () =>
    onComplete({ life_stage: lifeStage, household_type: household, priorities });

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-10 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full rounded-3xl border border-slate-800 bg-slate-900/70 backdrop-blur p-7 sm:p-9"
        data-testid="profile-setup"
      >
        <div className="w-14 h-14 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-5">
          <Compass className="w-7 h-7 text-emerald-400" />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-50">Let's tailor your journey</h1>
        <p className="text-slate-400 mt-2">
          A few quick details so your scenarios fit your life. All optional — skip any you'd rather not share.
        </p>

        <div className="mt-7 space-y-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-2.5">Life stage</p>
            <div className="flex flex-wrap gap-2">
              {LIFE_STAGES.map((s) => (
                <Chip key={s.v} testid={`life-stage-${s.v}`} active={lifeStage === s.v} onClick={() => setLifeStage(lifeStage === s.v ? null : s.v)}>
                  {s.l}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-2.5">Household</p>
            <div className="flex flex-wrap gap-2">
              {HOUSEHOLDS.map((h) => (
                <Chip key={h.v} testid={`household-${h.v}`} active={household === h.v} onClick={() => setHousehold(household === h.v ? null : h.v)}>
                  {h.l}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400 mb-2.5">Your priorities (pick any)</p>
            <div className="flex flex-wrap gap-2">
              {PRIORITIES.map((p) => (
                <Chip key={p} testid={`priority-${p}`} active={priorities.includes(p)} onClick={() => togglePriority(p)}>
                  {p}
                </Chip>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button
            data-testid="profile-skip-btn"
            onClick={() => onComplete(null)}
            className="text-sm text-slate-400 hover:text-emerald-400 underline underline-offset-4"
          >
            Skip for now
          </button>
          <Button
            data-testid="profile-continue-btn"
            onClick={submit}
            disabled={submitting}
            className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold rounded-xl shadow-lg shadow-emerald-500/25 disabled:opacity-60"
          >
            {submitting ? "Saving…" : "Continue"} <ArrowRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
};
